<template>
    <div class="layout-topbar">
        <router-link to="/" class="layout-topbar-logo">
            <img src="@/assets/images/Logo_FullColor_OnDark.svg" alt="Corridor logo" />
        </router-link>

        <div class="layout-topbar-menu">
            <template v-if="hasOperatorOrAdminRole">
                <router-link
                    to="/"
                    class="layout-topbar-button"
                    exact-active-class="active">
                    <i class="far fa-building"></i>
                    <span>Communities</span>
                </router-link>
                <router-link
                    to="/users"
                    class="layout-topbar-button"
                    exact-active-class="active">
                    <i class="fasl fa-users"></i>
                    <span>Users</span>
                </router-link>
            </template>
            <button
                type="button"
                class="profile-button"
                :aria-label="displayName"
                aria-haspopup="true"
                aria-controls="profile-menu"
                @click="profileMenu?.toggle($event)">
                <UserAvatar :user="user" />
            </button>
            <Menu
                id="profile-menu"
                ref="profileMenu"
                :model="profileMenuItems"
                popup>
                <template #submenuheader="{ item }">
                    <div>{{ item.label }}</div>
                    <div
                        v-if="user?.email && user.email !== item.label"
                        class="text-sm font-normal text-color-secondary">
                        {{ user.email }}
                    </div>
                </template>
            </Menu>
        </div>
        <EditProfileDialog v-model:visible="editProfileVisible" />
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import Menu from "primevue/menu";
import EditProfileDialog from "@/components/dialogs/EditProfileDialog.vue";
import UserAvatar from "@/components/shared/UserAvatar.vue";
import { useMe } from "@/composables/queries/useMe";
import { getUserDisplayName } from "@/utils/userNames";

const router = useRouter();
const { user, hasOperatorOrAdminRole } = useMe();

const displayName = computed(() =>
    getUserDisplayName({
        displayName: user.value?.displayName,
        firstName: user.value?.profile?.firstName,
        lastName: user.value?.profile?.lastName,
        email: user.value?.email,
    }),
);

const signOut = () => {
    router.push({ name: "signout" });
};

const profileMenu = ref<InstanceType<typeof Menu> | null>(null);
const editProfileVisible = ref(false);
const profileMenuItems = computed(() => [
    // An item with `items` renders as a non-clickable group header.
    { label: displayName.value, items: [] },
    { separator: true },
    {
        label: "Edit Profile",
        icon: "far fa-user-pen",
        command: () => {
            editProfileVisible.value = true;
        },
    },
    { separator: true },
    {
        label: "Sign Out",
        icon: "fasl fa-right-from-bracket",
        command: signOut,
    },
]);
</script>

<style scoped>
.layout-topbar {
    height: 4rem;
    background: var(--surface-card);
    border-bottom: 1px solid var(--surface-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 2rem;
}

.layout-topbar-logo {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: var(--text-color);
    font-weight: 500;
    font-size: 1.25rem;
}

.layout-topbar-logo img {
    height: 30px;
}

.layout-topbar-menu {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.layout-topbar-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    background: transparent;
    color: var(--text-color);
    cursor: pointer;
    text-decoration: none;
    border-radius: var(--border-radius);
    transition: background-color 0.2s;
}

.layout-topbar-button:hover {
    background-color: var(--surface-hover);
}

.profile-button {
    display: flex;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: 50%;
    cursor: pointer;
}

.profile-button:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
}

.layout-topbar-button.active {
    background-color: var(--primary-50);
    color: var(--primary-color);
    font-weight: 600;
}
</style>
